import { Box, Button, FormControl, FormHelperText, IconButton, InputAdornment, InputLabel, MenuItem, OutlinedInput, Select, Stack, Typography } from '@mui/material'
import { Close, DownloadOutlined, InsertDriveFileOutlined, UploadFileOutlined } from '@mui/icons-material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment from 'moment'
import { FunctionComponent, useContext, useEffect, useState } from 'react'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ReportPeriodSelectComponent } from '../../presentation-components/miscellaneous-components/report-period-select';
import { IReductionOffset, IPostReductionOffset, IReductionOffsetSubmitPayload } from '../../../shared/services/reduction-offset/reduction-offset.model';
import { ReportPeriodContext } from '../../../context/report-periods/report-periods';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import UploadedResourceService from '../../../shared/services/uploaded-resource/uploaded-resource.service';

const textFieldError = { color: "#bf3333", marginLeft: "16px !important" }

export const AddReductionOffsetDialog: FunctionComponent<{
  open: boolean,
  closeHandler: () => void,
  submitHandler: (payload: IReductionOffsetSubmitPayload, formikResetForm: () => void, isEdit: boolean) => void,
  newReductionOffset?: IReductionOffset,
  isEdit: boolean,
  isSubmitting: boolean,
}> = ({ open, closeHandler, submitHandler, newReductionOffset, isEdit = false, isSubmitting }) => {
  //Context
  const reportPeriodContext = useContext(ReportPeriodContext)
  //Services
  const uploadedResourceService = new UploadedResourceService()
  //State (certificate documents, REC pattern: new files staged locally, removals optimistic until save)
  const [fileData, setFileData] = useState<File[]>([])
  const [removedResourceIds, setRemovedResourceIds] = useState<string[]>([])
  const [openRetirementDate, setOpenRetirementDate] = useState<boolean>(false)

  useEffect(() => {
    if (newReductionOffset) {
      formik.setValues({
        name: newReductionOffset?.name,
        description: newReductionOffset?.description,
        amountTCE: newReductionOffset?.amountTCE,
        offsetType: newReductionOffset?.offsetType ?? '',
        vintage: newReductionOffset?.vintage ?? '',
        registry: newReductionOffset?.registry ?? '',
        standard: newReductionOffset?.standard ?? '',
        serialRange: newReductionOffset?.serialRange ?? '',
        projectReference: newReductionOffset?.projectReference ?? '',
        retirementDate: newReductionOffset?.retirementDate ?? '',
        retirementStatus: newReductionOffset?.retirementStatus ?? '',
        reportPeriodId: newReductionOffset?.reportPeriodId,
      })
    }
  }, [newReductionOffset])

  const customActionSchema = Yup.object().shape({
    name: Yup.string().required('You must provide a name'),
    description: Yup.string().nullable(),
    amountTCE: Yup.number().required('You must provide an amount'),
    offsetType: Yup.string().nullable(),
    vintage: Yup.string().nullable(),
    registry: Yup.string().nullable(),
    standard: Yup.string().nullable(),
    serialRange: Yup.string().nullable(),
    projectReference: Yup.string().nullable(),
    retirementDate: Yup.string().nullable(),
    retirementStatus: Yup.string().nullable(),
  })

  const formik = useFormik<IPostReductionOffset>({
    initialValues: {
      name: '',
      amountTCE: undefined,
      description: '',
      offsetType: '',
      vintage: '',
      registry: '',
      standard: '',
      serialRange: '',
      projectReference: '',
      retirementDate: '',
      retirementStatus: '',
      reportPeriodId: '',
    },
    validationSchema: customActionSchema,
    onSubmit: (values) => {
      values.reportPeriodId = reportPeriodContext.lastSelectedReportId!
      submitHandler({ values, files: fileData, removedResourceIds }, resetDialog, isEdit)
    }
  });

  const resetDialog = () => {
    formik.resetForm()
    setFileData([])
    setRemovedResourceIds([])
  }

  const cancelHandler = () => {
    resetDialog();
    closeHandler();
  }

  const addFilesHandler = (files: FileList | null) => {
    if (!files || files.length === 0) return
    setFileData(previous => [...previous, ...Array.from(files)])
  }

  const retainedResources = (newReductionOffset?.uploadedResources || [])
    .filter(resource => !removedResourceIds.includes(resource.id))

  const textField = (name: keyof IPostReductionOffset, label: string) => (
    <FormControl fullWidth variant='outlined'>
      <InputLabel htmlFor={name}>{label}</InputLabel>
      <OutlinedInput id={name} name={name} label={label}
        value={formik.values[name]}
        onChange={formik.handleChange}
        error={formik.touched[name] && Boolean(formik.errors[name])}
      ></OutlinedInput>
      {formik.touched[name] && formik.errors[name] &&
        <FormHelperText sx={textFieldError}>{formik.errors[name]}</FormHelperText>
      }
    </FormControl>
  )

  return (
    <DialogWrapper
      open={open}
      onClose={closeHandler}
      maxWidth='sm'
      title={!isEdit ? `Add Offset` : `Edit Offset`}
      cancelHandler={cancelHandler} submitHandler={formik.submitForm} isSubmitting={isSubmitting}>
      <Stack spacing={2} sx={{ mt: 1 }}>
        {textField('name', 'Name')}
        {textField('description', 'Description')}
        <FormControl fullWidth variant='outlined'>
          <InputLabel htmlFor='amountTCE'>Amount (tCO<sub>2</sub>e)</InputLabel>
          <OutlinedInput id='amountTCE' name='amountTCE' label='Amount (tCO2e)' type='number'
            value={formik.values.amountTCE}
            onChange={formik.handleChange}
            endAdornment={<InputAdornment position="end"><Typography variant='body2' sx={{ color: 'text.secondary' }}>tCO<sub>2</sub>e</Typography></InputAdornment>}
            error={formik.touched.amountTCE && Boolean(formik.errors.amountTCE)}
            inputProps={{ step: '0.0001', min: 0 }}
          ></OutlinedInput>
          {formik.touched.amountTCE && formik.errors.amountTCE &&
            <FormHelperText sx={textFieldError}>{formik.errors.amountTCE}</FormHelperText>
          }
        </FormControl>

        <Typography variant='subtitle2' sx={{ pt: 1 }}><b>Registry details (optional)</b></Typography>
        <Stack direction='row' spacing={2}>
          <FormControl fullWidth variant='outlined'>
            <InputLabel id='offsetType-label'>Type</InputLabel>
            <Select labelId='offsetType-label' id='offsetType' name='offsetType' label='Type'
              value={formik.values.offsetType}
              onChange={formik.handleChange}>
              <MenuItem value=''>Not specified</MenuItem>
              <MenuItem value='removal'>Removal</MenuItem>
              <MenuItem value='avoidance'>Avoidance</MenuItem>
            </Select>
          </FormControl>
          {textField('vintage', 'Vintage (year)')}
        </Stack>
        <Stack direction='row' spacing={2}>
          {textField('registry', 'Registry')}
          {textField('standard', 'Standard')}
        </Stack>
        <Stack direction='row' spacing={2}>
          {textField('serialRange', 'Serial range')}
          {textField('projectReference', 'Project reference')}
        </Stack>
        <Stack direction='row' spacing={2}>
          <FormControl fullWidth variant='outlined'>
            <InputLabel id='retirementStatus-label'>Retirement status</InputLabel>
            <Select labelId='retirementStatus-label' id='retirementStatus' name='retirementStatus' label='Retirement status'
              value={formik.values.retirementStatus}
              onChange={formik.handleChange}>
              <MenuItem value=''>Not specified</MenuItem>
              <MenuItem value='retired'>Retired</MenuItem>
              <MenuItem value='pending'>Pending</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth variant='outlined'>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label='Retirement date'
                value={formik.values.retirementDate ? moment(formik.values.retirementDate) : null}
                onChange={(date) => formik.setFieldValue('retirementDate', date && date.isValid() ? date.format('YYYY-MM-DD') : '')}
                format='DD/MM/YYYY'
                open={openRetirementDate}
                onOpen={() => setOpenRetirementDate(true)}
                onClose={() => setOpenRetirementDate(false)}
                slotProps={{
                  textField: {
                    onClick: () => setOpenRetirementDate(true),
                  },
                }}
              />
            </LocalizationProvider>
          </FormControl>
        </Stack>

        <Typography variant='subtitle2' sx={{ pt: 1 }}><b>Certificate (optional)</b></Typography>
        {(retainedResources.length > 0 || fileData.length > 0) &&
          <Stack spacing={1}>
            {retainedResources.map(resource => (
              <Box key={resource.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
                <InsertDriveFileOutlined fontSize='small' sx={{ color: 'text.secondary' }} />
                <Typography variant='body2' noWrap sx={{ flex: 1, minWidth: 0 }}>{resource.filename}</Typography>
                <IconButton size='small' onClick={() => uploadedResourceService.downloadFile(resource.id, resource.filename)}>
                  <DownloadOutlined fontSize='small' />
                </IconButton>
                <IconButton size='small' onClick={() => setRemovedResourceIds(previous => [...previous, resource.id])}>
                  <Close fontSize='small' />
                </IconButton>
              </Box>
            ))}
            {fileData.map((file, index) => (
              <Box key={`${file.name}-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
                <InsertDriveFileOutlined fontSize='small' sx={{ color: 'text.secondary' }} />
                <Typography variant='body2' noWrap sx={{ flex: 1, minWidth: 0 }}>{file.name}</Typography>
                <IconButton size='small' onClick={() => setFileData(previous => previous.filter((_, i) => i !== index))}>
                  <Close fontSize='small' />
                </IconButton>
              </Box>
            ))}
          </Stack>
        }
        <Button component='label' variant='outlined' startIcon={<UploadFileOutlined />} sx={{ alignSelf: 'flex-start' }}>
          Upload certificate
          <input type='file' hidden multiple
            accept='application/pdf,image/png,image/jpeg,image/gif,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            onChange={(event) => { addFilesHandler(event.target.files); event.target.value = '' }} />
        </Button>

        <ReportPeriodSelectComponent disabled />
      </Stack>
    </DialogWrapper >
  )
}
